import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash.debounce';
import Modal from 'components/shared/Modal';
import { ActivityIndicator, Button, Field } from '@kontentino/ui';
import { Asset } from 'types/Attachments';
import { MediaLibraryItemFileType } from 'types/MediaLibrary';
import { useUser } from 'modules/user/userSelector';
import { usePage } from 'modules/page/pageSelector';
import useAlbumTreeForMove from 'app/modules/mediaLibraryPage/hooks/useAlbumTreeForMove';
import useAlbumsForPage from 'app/modules/mediaLibraryPage/hooks/useAlbumsForPage';
import useAlbumAssets from 'app/modules/mediaLibraryPage/hooks/useAlbumAssets';
import {
  AssetFilters,
  AssetTypeFilter,
  DEFAULT_FILTERS,
} from 'app/modules/mediaLibraryPage/types/AssetFilters';
import AssetGrid from 'app/modules/mediaLibraryPage/components/grid/AssetGrid';
import EmptyState from 'app/modules/mediaLibraryPage/components/emptyState/EmptyState';
import SearchTextInput from 'app/components/SearchTextInput';
import MediaLibraryPickerFilters from 'app/modules/mediaLibraryPage/components/picker/MediaLibraryPickerFilters';
import MediaLibraryPickerAlbumSelect from 'app/modules/mediaLibraryPage/components/picker/MediaLibraryPickerAlbumSelect';
import MediaLibraryPickerProfileSelect from 'app/modules/mediaLibraryPage/components/picker/MediaLibraryPickerProfileSelect';
import { MediaLibraryPageConfig } from 'app/config/mediaLibraryPage';
import { AlbumScope } from 'app/modules/mediaLibraryPage/utils/albumScope';
import { useEffectOnce } from 'utils/hooks/useEffectOnce';

interface BaseProps {
  open: boolean;
  onClose(): void;
}

interface AddProps extends BaseProps {
  mode?: 'add';
  pageId: number;
  /** Maximum number of assets that may be selected (remaining capacity). */
  maxFiles: number;
  /** Restricts which asset kinds can be selected. */
  allowedFiles: MediaLibraryItemFileType[];
  onConfirm(assets: Asset[]): void;
}

interface MoveProps extends BaseProps {
  mode: 'move';
  pageId: AlbumScope;
  /** The album the picked files move into; it is left out of the album list. */
  targetAlbumId: string;
  onConfirm(assets: Asset[], sourceAlbumId: string): void;
}

type Props = AddProps | MoveProps;

const ALL_FILE_TYPES: MediaLibraryItemFileType[] = [
  'image',
  'video',
  'document',
];

/** Maps a composer file-type constraint to the asset-list `type` filter. */
function fileTypeToAssetFilter(
  file: MediaLibraryItemFileType,
): AssetTypeFilter[] {
  if (file === 'image') return ['images', 'gifs'];
  if (file === 'video') return ['videos'];
  return ['documents'];
}

/** Whether an asset is selectable given the allowed file constraints. */
function isAssetAllowed(
  asset: Asset,
  allowedFilters: Set<AssetTypeFilter>,
): boolean {
  const kind: AssetTypeFilter =
    asset.type === 'video'
      ? 'videos'
      : asset.type === 'gif'
      ? 'gifs'
      : asset.type === 'document'
      ? 'documents'
      : 'images';
  return allowedFilters.has(kind);
}

const MediaLibraryPicker: FC<Props> = (props) => {
  const { open, pageId, onClose } = props;
  const isMoveMode = props.mode === 'move';
  const moveTargetAlbumId = props.mode === 'move' ? props.targetAlbumId : null;
  const maxFiles =
    props.mode === 'move' ? Number.POSITIVE_INFINITY : props.maxFiles;
  const allowedFiles =
    props.mode === 'move' ? ALL_FILE_TYPES : props.allowedFiles;
  const { t } = useTranslation();
  const currentUserId = useUser().id;
  const { pages } = usePage();

  // The picker can browse any page's library, not just the post's page.
  const [activePageId, setActivePageId] = useState<AlbumScope>(pageId);

  const albumsQuery = useAlbumsForPage(open ? activePageId : undefined);
  const albumTreeQuery = useAlbumTreeForMove(activePageId, open);
  const isAlbumTreeError = albumTreeQuery.isError && !albumTreeQuery.data;

  const [activeAlbumId, setActiveAlbumId] = useState<string | undefined>(
    undefined,
  );
  const [filters, setFilters] = useState<AssetFilters>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<Asset[]>([]);
  const [searchValue, setSearchValue] = useState(filters.search ?? '');

  const notifySearchChanged = useRef(
    _debounce((value: string) => {
      setFilters((prev) => ({ ...prev, search: value || undefined }));
    }, MediaLibraryPageConfig.SEARCH_DEBOUNCE_MS),
  );

  useEffectOnce(() => {
    return () => {
      notifySearchChanged.current.cancel();
    };
  });

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    notifySearchChanged.current(value);
  };

  const profileOptions = useMemo(
    () =>
      pages.map((page) => ({
        id: page.id,
        name: page.name,
        logoSrc: page.logo?.src,
        type: page.type,
        isGlobal: page.isGlobal,
      })),
    [pages],
  );

  const allowedFilters = useMemo(
    () => new Set(allowedFiles.flatMap(fileTypeToAssetFilter)),
    [allowedFiles],
  );

  const albumTree = useMemo(
    () =>
      (albumTreeQuery.data ?? []).filter(
        ({ album }) => album.id !== moveTargetAlbumId,
      ),
    [albumTreeQuery.data, moveTargetAlbumId],
  );

  // Default to the page's default album once albums load; in move mode, to
  // the first album other than the one being filled.
  useEffect(() => {
    if (!open) return;
    if (activeAlbumId) return;
    const initialAlbum = moveTargetAlbumId
      ? albumTree[0]?.album
      : albumsQuery.data?.find((a) => a.is_default) ?? albumsQuery.data?.[0];
    if (initialAlbum) setActiveAlbumId(initialAlbum.id);
  }, [open, activeAlbumId, albumsQuery.data, albumTree, moveTargetAlbumId]);

  // Reset transient state whenever the modal is reopened.
  useEffect(() => {
    if (open) return;
    setActiveAlbumId(undefined);
    setFilters(DEFAULT_FILTERS);
    setSearchValue('');
    setSelected([]);
  }, [open]);

  // Each open starts on the post's own page.
  useEffect(() => {
    if (open) setActivePageId(pageId);
  }, [open, pageId]);

  // Switching the browsed page re-selects that page's default album.
  useEffect(() => {
    setActiveAlbumId(undefined);
    if (isMoveMode) setSelected([]);
  }, [activePageId, isMoveMode]);

  const assetsQuery = useAlbumAssets({
    albumId: activeAlbumId,
    filters,
    currentUserId,
  });

  const assets = useMemo(
    () => (assetsQuery.data?.pages ?? []).flatMap((page) => page.data),
    [assetsQuery.data],
  );

  const handleReachEnd = () => {
    if (assetsQuery.hasNextPage && !assetsQuery.isFetchingNextPage) {
      assetsQuery.fetchNextPage();
    }
  };

  const selectedIds = useMemo(
    () => new Set(selected.map((asset) => asset.id)),
    [selected],
  );

  const isSingleSelect = maxFiles <= 1;

  const handleSelect = (asset: Asset, event?: MouseEvent) => {
    event?.stopPropagation();
    if (maxFiles < 1 || !isAssetAllowed(asset, allowedFilters)) return;

    setSelected((prev) => {
      const exists = prev.some((a) => a.id === asset.id);
      if (exists) return prev.filter((a) => a.id !== asset.id);
      if (isSingleSelect) return [asset];
      if (prev.length >= maxFiles) return prev;
      return [...prev, asset];
    });
  };

  // Move mode names the browsed album as the source of every picked file, so a
  // selection never spans albums.
  const handleSelectAlbum = (albumId: string) => {
    if (isMoveMode && albumId !== activeAlbumId) setSelected([]);
    setActiveAlbumId(albumId);
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    if (props.mode === 'move') {
      if (activeAlbumId) props.onConfirm(selected, activeAlbumId);
      return;
    }
    props.onConfirm(selected);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="responsive"
      isContentHeightAutoResizable={false}
      blockScroll={false}
      focusTrapped={false}
      classNames={{
        root: '!tw-left-0',
        modalContainer: '!tw-flex tw-items-center tw-justify-center',
        modal:
          '!tw-flex tw-flex-col max-xl:tw-h-full !tw-max-h-full xl:!tw-max-h-[min(800px,90dvh)] xl:tw-min-h-[min(28rem,90dvh)] !tw-w-full !tw-max-w-[768px] !tw-m-0 xl:!tw-m-auto tw-relative !tw-rounded-xl',
      }}
    >
      <Modal.Header title={t('fromYourMediaLibrary')} />
      <Modal.Content className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-gap-4">
          <aside
            className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-w-[240px] tw-shrink-0 tw-overflow-auto tw-p-2"
            data-cy="media-library-picker_albums"
          >
            <Field.Label required className="tw-mb-2">
              {t('profile')}
            </Field.Label>
            <MediaLibraryPickerProfileSelect
              profiles={profileOptions}
              activeProfileId={activePageId}
              onSelect={setActivePageId}
            />
            <Field.Label required className="tw-mb-2 tw-mt-4">
              {t('mediaLibrary.picker.album')}
            </Field.Label>
            <MediaLibraryPickerAlbumSelect
              albumTree={albumTree}
              activeAlbumId={activeAlbumId}
              isLoading={albumTreeQuery.isLoading}
              isError={isAlbumTreeError}
              onRetry={() => albumTreeQuery.refetch()}
              onSelect={handleSelectAlbum}
            />
            <div className="tw-mt-3">
              <SearchTextInput
                value={searchValue}
                onChange={(event) =>
                  handleSearchValueChange(event.target.value)
                }
                onClear={() => handleSearchValueChange('')}
                placeholder={t('search')}
                data-cy="media-library-picker_search-input"
              />
            </div>
            <MediaLibraryPickerFilters
              type={filters.type}
              uploadedBy={filters.uploadedBy}
              sort={filters.sort}
              onChangeType={(type) => setFilters((prev) => ({ ...prev, type }))}
              onChangeUploadedBy={(uploadedBy) =>
                setFilters((prev) => ({ ...prev, uploadedBy }))
              }
              onChangeSort={(by, direction) =>
                setFilters((prev) => ({
                  ...prev,
                  sort: { by, direction: direction ?? prev.sort.direction },
                }))
              }
            />
          </aside>

          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
            <div
              className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-p-2"
              data-cy="media-library-picker_grid"
            >
              {assetsQuery.isLoading ? (
                <ActivityIndicator />
              ) : assets.length === 0 && isMoveMode && isAlbumTreeError ? (
                <div
                  className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-20"
                  data-cy="media-library-picker_grid-error"
                >
                  <span className="tw-text-red-100 tw-text-sm">
                    {t('somethingWentWrong')}
                  </span>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => albumTreeQuery.refetch()}
                    data-cy="media-library-picker_grid-retry"
                  >
                    {t('retry')}
                  </Button>
                </div>
              ) : assets.length === 0 ? (
                <EmptyState
                  variant={filters.search ? 'no-results' : 'no-files'}
                />
              ) : (
                <AssetGrid
                  assets={assets}
                  selectedAssetIds={selectedIds}
                  onSelectAsset={handleSelect}
                  onOpenAsset={(asset) => handleSelect(asset)}
                  columns={4}
                  onReachEnd={
                    assetsQuery.hasNextPage ? handleReachEnd : undefined
                  }
                  isLoadingMore={assetsQuery.isFetchingNextPage}
                />
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer withDivider>
        <span className="tw-mr-auto tw-text-sm tw-text-grayscale-120">
          {isMoveMode
            ? t('mediaLibrary.page.selection.count', {
                count: selected.length,
              })
            : t('mediaLibrary.picker.selectedCount', {
                count: selected.length,
                max: maxFiles,
              })}
        </span>
        <Button variant="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={selected.length === 0}
          data-cy="media-library-picker_confirm"
        >
          {isMoveMode
            ? t('mediaLibrary.picker.move', { count: selected.length })
            : t('mediaLibrary.picker.confirm', {
                defaultValue: 'Add {{count}}',
                count: selected.length,
              })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { isAssetAllowed, fileTypeToAssetFilter };
export default MediaLibraryPicker;
